import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { TrafficAllocationDropdown } from '@app/react-common/components/traffic-allocation/TrafficAlloctionDropdown';
import {
  TrafficAllocationVariant,
  findControlGroupTypeLabel,
  matchesControlGroupType,
} from '@app/react-common/components/traffic-allocation/TrafficAllocationShared';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { AbTestTypeCategory } from '../../../models/AbTestCreationType';
import { AbTestFormData } from '../AbTestModal';
import Button from '@app/react-common/components/button/Button';

interface ExtensionsGroupsAllocationSectionProps {
  formData: AbTestFormData;
  setFormData: (data: any) => void;
  setTrafficAllocationValid: (valid: boolean) => void;
  mode: string;
  shop: Shop;
}

// NONE (widgetExternalId === null) is control-only, and a test group has to share the control's group type
function isAddableTestGroup(variant: TrafficAllocationVariant, controlGroupTypeLabel: string | null): boolean {
  return (
    !variant.isSelected && variant.widgetExternalId !== null && matchesControlGroupType(variant, controlGroupTypeLabel)
  );
}

export const ExtensionsGroupsAllocationSection: React.FC<ExtensionsGroupsAllocationSectionProps> = React.memo(
  ({ formData, setFormData, setTrafficAllocationValid, mode, shop }) => {
    const handleAddTestGroup = useCallback(() => {
      const currentVariants = formData?.abTestVariants || [];
      const selectedVariants = currentVariants.filter((v) => v.isSelected);

      // Find the maximum slot index, ensuring we start from 1 (0 is for Control group)
      const maxSlotIndex = selectedVariants.reduce((max, v) => {
        const slotIdx = v.slotIndex ?? 0;
        return Math.max(max, slotIdx);
      }, 0);
      const nextSlotIndex = maxSlotIndex + 1;
      const selectedCount = selectedVariants.length;
      const newPercentage = Math.floor(100 / (selectedCount + 1));

      // Exclude NONE variant (widgetExternalId === null) as it can only be used for Control group
      const unselectedVariant = currentVariants.find((v) =>
        isAddableTestGroup(v, findControlGroupTypeLabel(currentVariants)),
      );

      if (unselectedVariant) {
        const updatedVariants = currentVariants.map((variant) => {
          // Match by widgetExternalId instead of internalId to avoid issues
          if (variant.widgetExternalId === unselectedVariant.widgetExternalId) {
            return {
              ...variant,
              isSelected: true,
              slotIndex: nextSlotIndex,
              percentageTraffic: newPercentage,
            };
          }
          // Redistribute percentages for existing selected variants
          if (variant.isSelected) {
            return {
              ...variant,
              percentageTraffic: newPercentage,
            };
          }
          return variant;
        });

        // Update formData directly - this will trigger TrafficAllocationDropdown to update via props
        setFormData((prevData) => ({ ...prevData, abTestVariants: updatedVariants }));
      }
    }, [formData?.abTestVariants, setFormData]);

    // Memoize computed values to avoid recalculating on every render
    const hasExtensionGroups = useMemo(
      () => formData?.abTestVariants && formData.abTestVariants.length > 0,
      [formData?.abTestVariants],
    );

    const availableVariants = useMemo(() => {
      const variants = formData?.abTestVariants || [];
      const controlGroupTypeLabel = findControlGroupTypeLabel(variants);
      return variants.filter((v) => isAddableTestGroup(v, controlGroupTypeLabel)).length;
    }, [formData?.abTestVariants]);

    // Always show the button if there are extension groups, but disable it if all are selected
    const canAddMoreGroups = useMemo(
      () => hasExtensionGroups && availableVariants > 0,
      [hasExtensionGroups, availableVariants],
    );

    const handleChange = useCallback(
      (valid: boolean, variants: TrafficAllocationVariant[]) => {
        setTrafficAllocationValid(valid);
        setFormData((prevData) => ({ ...prevData, abTestVariants: variants }));
      },
      [setTrafficAllocationValid, setFormData],
    );

    return (
      <>
        <TrafficAllocationDropdown
          trafficAllocationConfig={{
            variants: formData?.abTestVariants || [],
          }}
          onChange={handleChange}
          viewMode={mode === 'view'}
          shop={shop}
          testTypeCategory={AbTestTypeCategory.EXTENSIONS_GROUPS}
          placeholder="Choose an extension group as a Control Group"
        />
        {mode !== 'view' && hasExtensionGroups && (
          <div className="extensions-groups-allocation-section__add-button">
            <Button
              onClick={handleAddTestGroup}
              text={'+ Add Test Group'}
              size={'medium'}
              variant={'ghost-light'}
              disabled={!canAddMoreGroups}
            />
          </div>
        )}
      </>
    );
  },
);
